import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';

export interface FooterLink {
  label: React.ReactNode;
  to?: string;
  href?: string;
  external?: boolean;
}

export interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  productName?: string;
  version?: string;
  /** Optional commit SHA / build number shown as a secondary badge. */
  build?: string;
  links?: FooterLink[];
  /** Show the "Status: Operational" dot + link. Pass `href` to customize. */
  statusHref?: string;
  /** Override the copyright line. Defaults to `© <year> <productName>`. */
  legal?: React.ReactNode;
}

const DEFAULT_LINKS: FooterLink[] = [
  { label: 'Docs', href: 'https://docs.example.com', external: true },
  { label: 'Support', href: 'mailto:support@example.com', external: true },
  { label: 'Privacy', to: '/legal/privacy' },
  { label: 'Terms', to: '/legal/terms' },
];

/**
 * Slim application footer. Provides the version / build badge,
 * support and legal links, and an optional status-page indicator.
 *
 * Intentionally unopinionated about layout — parent controls width
 * and spacing. Do not wrap in <main>; render as a sibling of the
 * routed page content so it doesn't scroll inside dialogs.
 */
export function Footer({
  productName = 'KomplyStack',
  version,
  build,
  links = DEFAULT_LINKS,
  statusHref,
  legal,
  className,
  ...rest
}: FooterProps) {
  const year = new Date().getFullYear();
  return (
    <footer
      role="contentinfo"
      className={cn(
        'mt-auto border-t border-neutral-200 dark:border-neutral-800 bg-white/60 dark:bg-neutral-900/60 backdrop-blur',
        'dark:border-neutral-800 dark:bg-neutral-950/60',
        'px-6 py-4 text-xs text-neutral-500 dark:text-neutral-400',
        className
      )}
      {...rest}
    >
      <div className="mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between max-w-[1400px]">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-neutral-600 dark:text-neutral-400 dark:text-neutral-300">
            {legal ?? `© ${year} ${productName}`}
          </span>
          {version && (
            <span
              className="rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/60 px-1.5 py-0.5 font-mono text-[10px] text-neutral-600 dark:text-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
              title={build ? `Build ${build}` : undefined}
            >
              v{version}
              {build && (
                <span className="ml-1 text-neutral-400 dark:text-neutral-500">
                  · {build.slice(0, 7)}
                </span>
              )}
            </span>
          )}
          {statusHref && (
            <a
              href={statusHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
              <span>Status</span>
            </a>
          )}
        </div>
        <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {links.map((link, i) => {
            const cls =
              'hover:text-neutral-800 hover:underline dark:hover:text-neutral-100';
            if (link.href) {
              return (
                <a
                  key={i}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  className={cls}
                >
                  {link.label}
                </a>
              );
            }
            if (link.to) {
              return (
                <Link key={i} to={link.to} className={cls}>
                  {link.label}
                </Link>
              );
            }
            return (
              <span key={i} className="text-neutral-400 dark:text-neutral-500 dark:text-neutral-600">
                {link.label}
              </span>
            );
          })}
        </nav>
      </div>
    </footer>
  );
}

export default Footer;
